/**
 * Package palette / canvas sync helpers for canonical node type strings.
 *
 * Canonical node types come in two flavours:
 *
 *   - **Versioned**:   ``<packageId>/<templateId>@<major>``
 *   - **Unversioned**: ``<packageId>/<templateId>``
 *
 * Unversioned refs (the default form used in trill files and the
 * pre-installed builtin package) resolve to the latest installed major via
 * the registry's unversioned index — see {@link getNodeDescriptor}.
 */

/**
 * The pre-installed package every Curio ships with.
 *
 * Lives here rather than in ``packagesClient`` because the rules that key off
 * it -- "always in the palette regardless of the project lockfile", "no PACKAGE
 * pill" -- are read by modules ``packagesClient`` imports, which cannot import
 * it back. This module has no imports of its own, so it is safe from anywhere.
 */
export const BUILTIN_PACKAGE_ID = 'curio.builtin';

/** `it.vendor.package/foo-kind@3` → `it.vendor.package@3` */
export function packageKeyFromCanonicalNodeType(nodeType: string | undefined | null): string | null {
  if (nodeType == null || typeof nodeType !== 'string') return null;
  const m = nodeType.match(/^(.+)\/([^/@]+)@(\d+)$/);
  if (!m) return null;
  return `${m[1]}@${m[3]}`;
}

/** `it.vendor.package/foo-kind@3` → `{ unversioned: 'it.vendor.package/foo-kind', major: 3 }`. */
export function splitCanonicalNodeType(nodeType: string): { unversioned: string; major: number } | null {
  if (typeof nodeType !== 'string') return null;
  const m = nodeType.match(/^(.+)\/([^/@]+)@(\d+)$/);
  if (!m) return null;
  return { unversioned: `${m[1]}/${m[2]}`, major: Number(m[3]) };
}
